/**
 * Utility for matching EOR codes from line data to code lists
 * Includes validation to prevent DoS attacks
 */
export class EorCodeMatcher {
  /**
   * Match comma-separated code IDs from line data to a list of code objects
   * Validates input to prevent DoS attacks
   *
   * @param codeIdsString - Comma-separated string of code IDs (e.g., "A1,B2,C3")
   * @param codesList - Array of code objects with codeId property
   * @returns Array of matched code objects
   */
  static matchCodes(
    codeIdsString: string | undefined,
    codesList: any[] | undefined,
  ): any[] {
    if (!codeIdsString || !codesList || codesList.length === 0) {
      return [];
    }

    // Validate input string length to prevent DoS
    if (codeIdsString.length > 1000) {
      return [];
    }

    // Split comma-separated IDs and trim whitespace
    const ids = codeIdsString.split(',').map((id) => id.trim());

    // Limit number of IDs to prevent DoS
    if (ids.length > 100) {
      return [];
    }

    // Validate each ID format (alphanumeric only)
    const validIds = ids.filter(
      (id) => /^[a-zA-Z0-9]+$/.test(id) && id.length <= 50,
    );

    // Find matching codes
    return codesList.filter((code) => validIds.includes(code.codeId));
  }

  /**
   * Get reduction codes for a specific line
   */
  static getReductionCodes(
    lineData: any,
    reductionCodes: any[] | undefined,
  ): any[] {
    return this.matchCodes(lineData.reductionCodes, reductionCodes);
  }

  /**
   * Get reason codes for a specific line
   */
  static getReasonCodes(lineData: any, reasonCodes: any[] | undefined): any[] {
    return this.matchCodes(lineData.reasonCodes, reasonCodes);
  }

  /**
   * Get group codes for a specific line
   */
  static getGroupCodes(lineData: any, groupCodes: any[] | undefined): any[] {
    return this.matchCodes(lineData.groupCodes, groupCodes);
  }
}
